import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

// Bug #4 — Tarif prépresse : 24 € jusqu'à 4 pages, puis +1,50 €/page supplémentaire.
const BASE_PRICE = 24;
const EXTRA_PRICE = 1.5;
const FREE_PAGES = 4;

function prepressePrice(pages) {
  const p = Math.max(1, Number(pages) || 1);
  return BASE_PRICE + Math.max(0, p - FREE_PAGES) * EXTRA_PRICE;
}

function fmtEUR(n) {
  const v = Number(n) || 0;
  if (Math.abs(v - Math.round(v)) < 0.005) return `${Math.round(v)} €`;
  return `${v.toFixed(2).replace(".", ",")} €`;
}

export default function PrepresseConfigurator() {
  const nav = useNavigate();
  const [pages, setPages] = useState("1");
  const [delay, setDelay] = useState("standard");
  const [notes, setNotes] = useState("");
  const [technical, setTechnical] = useState("");
  const [files, setFiles] = useState([]);
  const [confirmChk, setConfirmChk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileError, setFileError] = useState("");

  const price = useMemo(() => prepressePrice(pages), [pages]);
  const nbPages = Math.max(1, Number(pages) || 1);
  const extra = Math.max(0, nbPages - FREE_PAGES);

  const submit = async () => {
    if (!files.length) {
      setFileError("Merci de joindre le fichier à contrôler avant d'envoyer votre demande.");
      toast.error("Fichier obligatoire");
      return;
    }
    if (!confirmChk) {
      toast.error("Cochez la confirmation pour envoyer votre demande.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/prepresse/order", {
        pages: nbPages,
        delay,
        notes: notes.trim(),
        technical: technical.trim(),
        file_ids: files.map((f) => f.id).filter(Boolean),
        confirm: true,
      });
      toast.success("Votre demande a bien été enregistrée.");
      nav(`/espace/commande/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-cream min-h-[85vh] py-14" data-testid="page-prepresse-configurator">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
        <Link to="/preparer-mon-projet" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Retour
        </Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Contrôle prépresse</div>
          <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Vérifier mon fichier avant impression</h1>
          <p className="mt-4 text-neutral-700 max-w-2xl text-[15px] leading-relaxed">
            Diagnostic technique de votre fichier avant envoi à l'imprimeur : fonds perdus, résolution, CMJN, polices, PDF/X, marges…
            Vous recevez un rapport clair listant les points à vérifier ou corriger.
            <br /><br />
            <strong>Cette prestation est un diagnostic.</strong> Si votre fichier doit être corrigé, orientez-vous vers
            <Link to="/configurer/conforme" className="cs-link-underline text-[#0E7C90]"> « Rendre mon fichier conforme à l'imprimeur »</Link> (29 €).
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_300px] gap-8">
          <div className="space-y-6">
            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 1 · Nombre de pages</div>
              <label className="block">
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">
                  Combien de pages contient votre document ?
                </span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={pages}
                  onChange={(e) => setPages(e.target.value)}
                  className="cs-input mt-2 w-full"
                  data-testid="prepresse-pages"
                />
                <span className="block text-[12px] text-neutral-500 mt-1">
                  Tarif : 24 € jusqu'à 4 pages, puis +1,50 € par page supplémentaire.
                </span>
              </label>
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 2 · Votre fichier</div>
              <p className="text-[13px] text-neutral-600">
                Envoyez le fichier à contrôler (PDF recommandé). <strong className="text-black">Obligatoire.</strong>
              </p>
              <FileUploader files={files} setFiles={(fn) => { setFileError(""); setFiles(fn); }} testId="prepresse-file" />
              {fileError && (
                <div role="alert" aria-live="polite" className="text-[13px] text-red-700 border border-red-400 bg-red-50 px-3 py-2" data-testid="prepresse-file-error">
                  {fileError}
                </div>
              )}
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 3 · Délai</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "standard", l: "Standard — 24 à 48 h" },
                  { k: "urgent", l: "Urgent — même jour (selon disponibilité)" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setDelay(o.k)}
                    data-testid={`prepresse-delay-${o.k}`}
                    className={`px-4 py-2 border text-[13px] ${delay === o.k ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <p className="text-[12px] font-mono text-neutral-500">
                Un délai urgent peut nécessiter un supplément, communiqué avant toute intervention.
              </p>
            </section>

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Étape 4 · Consignes & données techniques</div>
              <label className="block">
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Consignes de votre imprimeur (facultatif)</span>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="cs-input mt-2 w-full resize-none"
                  data-testid="prepresse-notes"
                  placeholder="Ex. : format A5, fond perdu 3 mm, PDF/X-4, CMJN…"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Données techniques du support (facultatif)</span>
                <textarea
                  rows={3}
                  value={technical}
                  onChange={(e) => setTechnical(e.target.value)}
                  className="cs-input mt-2 w-full resize-none"
                  data-testid="prepresse-technical"
                  placeholder="Ex. : reliure dos carré, 120 g couché, quadri recto-verso…"
                />
              </label>
            </section>

            <section className="cs-card p-6 space-y-3">
              <div className="cs-eyebrow text-neutral-500">Étape 5 · Confirmation</div>
              <label className="flex items-start gap-3 text-[13px]">
                <input
                  type="checkbox"
                  checked={confirmChk}
                  onChange={(e) => setConfirmChk(e.target.checked)}
                  data-testid="prepresse-confirm"
                  className="mt-1"
                />
                <span>Je confirme que le fichier et les informations transmis correspondent à ma demande.</span>
              </label>
              <button
                onClick={submit}
                disabled={!confirmChk || submitting || !files.length}
                data-testid="prepresse-submit"
                className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Envoi…" : `Confirmer ma demande — ${fmtEUR(price)}`}
              </button>
            </section>
          </div>

          <aside className="lg:sticky lg:top-4 h-fit">
            <div className="cs-card p-5" data-testid="prepresse-price-panel">
              <div className="cs-eyebrow text-neutral-500">Tarif</div>
              <div className="mt-3 font-serif text-4xl" data-testid="prepresse-price">{fmtEUR(price)}</div>
              <div className="mt-3 text-[12px] text-neutral-600 leading-relaxed">
                Base : {fmtEUR(BASE_PRICE)} jusqu'à {FREE_PAGES} pages.<br />
                {extra > 0 ? `+ ${extra} page(s) × 1,50 €` : "Pas de supplément."}
              </div>
              <ul className="mt-4 text-[13px] space-y-1">
                {["Diagnostic technique complet", "Rapport détaillé", "Vérification fonds perdus / résolution / CMJN / polices"].map((x, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={14} className="text-[#0E7C90] mt-0.5" />{x}</li>
                ))}
              </ul>
              <p className="mt-3 text-[12px] font-mono text-neutral-500 leading-relaxed">
                Prestation de diagnostic — la correction du fichier n'est pas incluse.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
